// LangRouteLayout.jsx
'use strict';
import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import {
  PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG,
  normalizeLang,
  removeLangFromPath,
  generateLangPath,
} from '../resource/langPath.js';

/**
 * LangRouteLayout - Layout component for multi-language routing
 * Using Optional Segments (:lang?) to handle language prefixes
 *
 * Route rules:
 * - Traditional Chinese (zh-TW, default): /xxxxx/xxx (no prefix)
 * - English (en): /en/xxxxx/xxx
 * - Japanese (ja): /ja/xxxxx/xxx
 * - Other non-default languages: /{lang}/xxxxx/xxx
 */
const LangRouteLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { lang: langParam } = useParams();

  // Get language from URL param (from :lang? optional segment)
  // Only treat as valid lang if it's in PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG
  const langInUrl = useMemo(() => {
    if (!langParam) return null;
    const normalized = langParam.toLowerCase();
    return PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.includes(normalized)
      ? normalized
      : null;
  }, [langParam]);

  // Language sync strategy:
  // - SSR: URL is the source of truth (for SEO, /en shows English)
  // - CSR: i18n automatically uses cookie language (no redirect needed)

  // Update URL when i18n language changes
  useEffect(() => {
    const handleLanguageChanged = newLang => {
      const normalizedNewLang = normalizeLang(newLang);
      const currentPath = removeLangFromPath(location.pathname);
      const newPath = generateLangPath(currentPath, normalizedNewLang);

      if (location.pathname !== newPath) {
        const shouldNavigate =
          (!langInUrl &&
            PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.includes(
              normalizedNewLang
            )) ||
          (langInUrl && normalizedNewLang === DEFAULT_LANGUAGE) ||
          (langInUrl && langInUrl !== normalizedNewLang);

        if (shouldNavigate) {
          navigate(newPath + location.search + location.hash, {
            replace: true,
          });
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [langInUrl, location, navigate, i18n]);

  return <Outlet />;
};

export default LangRouteLayout;
